// ----------------------------------------------------------------
// <copyright company='Microsoft Corporation'>
//     Copyright (c) Microsoft Corporation.  All rights reserved.
// </copyright>
// ----------------------------------------------------------------

import React, {
    useEffect,
    useState,
} from 'react';
import clsx from 'clsx';
import {
    Checkbox,
    ComboBox,
    Dialog,
    DialogFooter,
    DialogType,
    IComboBoxOption,
    IComboBoxStyles,
    Label,
    Spinner,
    SpinnerSize,
    TextField,
    TooltipHost,
    Icon,
} from 'office-ui-fabric-react';

import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { uniqBy, snakeCase } from 'lodash';
import Message from './../../components/Messages';
import Environment from './../../environments';
import { isUserSuperAdmin } from './../../helpers/authorization';
import { actionStatusSelector, LoadingState } from './../../store/actionStatus';
import {
    GET_TIERS,
    POST_USER,
    allTierSelector,
    channelsSelector,
    clearUserMessage,
    createUserStatusSelector,
    getTiers,
    postUser,
    rolesSelector,
    soldToShipToSelector,
} from './../../store/manageUsers';
import { userInfoSelector } from './../../store/user';
import {
    postAddAadUser,
    POST_ADD_AAD_USER,
    addAadUserStatusSelector,
    clearAadUserMessage,
} from './../../store/manageAadOps';
import { validateEmail } from './../../helpers/validation';
import Styles from './manageUsers.module.scss';

const dropdownStyles: Partial<IComboBoxStyles> = {
    container: {
        minWidth: 100,
    },
    errorMessage: {
        fontSize: 12,
    },
    optionsContainerWrapper: {
        maxHeight: 350,
    },
};

export interface RetailUserType {
    channelName?: string;
    channelId: string | number;
    channelTierId?: string;
    comment?: string;
    createdEmailId?: string;
    domainAlias?: string;
    email: string | undefined;
    firstName: string | undefined;
    isActive?: boolean;
    isWarrantyUpdateEnabled: boolean;
    lastName: string | undefined;
    phone?: string;
    puid?: string;
    retailUserId: number;
    roleId: string | number;
    roleName?: string;
    shiptoCustomerNbr?: string | number;
    soldToCustomerNbr?: string | number;
    tierName: string | number;
    tierId?: string;
    aadUserId?: string;
    inviteRedeemUrl?: string;
}

export interface ManageUserPropTypes {
    hide: boolean;
    onCloseOrOpen: (flag: boolean) => void;
    retailUser?: RetailUserType;
    userInfo?: any;
    isEdit: boolean;
}

const initialNewUserState: RetailUserType = {
    channelId: '',
    channelName: '',
    createdEmailId: '',
    domainAlias: '',
    email: '',
    firstName: '',
    isActive: true,
    isWarrantyUpdateEnabled: false,
    lastName: '',
    retailUserId: 0,
    roleId: '',
    roleName: '',
    shiptoCustomerNbr: '',
    soldToCustomerNbr: '',
    tierId: '',
    tierName: '',
    aadUserId: '',
    inviteRedeemUrl: '',
};

const initialValidationState = {
    channelName: undefined,
    email: undefined,
    firstName: undefined,
    lastName: undefined,
    roleName: undefined,
    shiptoCustomerNbr: undefined,
    soldToCustomerNbr: undefined,
    tierName: undefined,
};

export default function AddOrUpdate(props: ManageUserPropTypes) {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const { isEdit } = props;

    const [email, setEmail] = useState<string | undefined>('');
    const [enableWarrantyUpdate, setEnableWarrantyUpdate] = useState(false);
    const [firstName, setFirstName] = useState<string | undefined>('');
    const [hide, setHideDialog] = useState(props.hide);
    const [hideChildDialog, setHideChildDialog] = useState<boolean>(false);
    const [lastName, setLastName] = useState<string | undefined>('');
    const [retailUser, setRetailUser] = useState<RetailUserType>(initialNewUserState);
    const [selectedChannel, setSelectedChannel] = useState<any>(undefined);
    const [selectedRole, setSelectedRole] = useState<any>(undefined);
    const [selectedShipTo, setSelectedShipTo] = useState<any>(undefined);
    const [selectedSoldTo, setSelectedSoldTo] = useState<any>(undefined);
    const [selectedTier, setSelectedTier] = useState<any>(undefined);
    const [submitted, setSubmitted] = useState(false);
    const [validation, setValidation] = useState<any>(initialValidationState);
    const [inviteRedeemUrl, setInviteRedeemUrl] = useState<any>('');
    const [copyStatus, setCopyStatus] = useState<any>('Copy Link');

    const userInfo = useSelector(state => userInfoSelector(state));
    const tiersStatus = useSelector(state => actionStatusSelector(state)(GET_TIERS));
    const roles = useSelector(state => rolesSelector(state));
    const channels = useSelector(state => channelsSelector(state));
    const tiers = useSelector(state => allTierSelector(state));
    const soldToShipToData = useSelector(state => soldToShipToSelector(state));
    const createStatus = useSelector(state => actionStatusSelector(state)(POST_USER));
    const userCreationResponse = useSelector(state => createUserStatusSelector(state));
    const addAadUserStatus = useSelector(state => actionStatusSelector(state)(POST_ADD_AAD_USER));
    const addAadUserResponse = useSelector(state => addAadUserStatusSelector(state));

    const tiersLoading = tiersStatus.status === LoadingState.started || tiersStatus.status === LoadingState.notStarted;
    const isCreatingUpdatingUser = createStatus.status === LoadingState.started || tiersLoading;
    const isAddingAadUser = addAadUserStatus.status === LoadingState.started;
    const isAutoOnboarding = Environment.isAutoUserOnboarding;

    useEffect(() => {
        setHideDialog(props.hide);
        if (!props.hide) {
            setValidation(initialValidationState);
            setSelectedTier(undefined);
        }
    }, [props.hide]);

    useEffect(() => {
        if (addAadUserResponse && addAadUserResponse.status && addAadUserResponse.status !== '' && addAadUserResponse.status.toLowerCase() === 'succeeded') {
            if (addAadUserResponse.errorInfo === '') {
                retailUser.aadUserId = addAadUserResponse.retailUser.aadUserId;
                if (addAadUserResponse.retailUser.inviteRedeemUrl && addAadUserResponse.retailUser.inviteRedeemUrl !== '') {
                    setInviteRedeemUrl(addAadUserResponse.retailUser.inviteRedeemUrl);
                    retailUser.inviteRedeemUrl = addAadUserResponse.retailUser.inviteRedeemUrl;
                }
                else {
                    setInviteRedeemUrl(t('user_exists_on_robo_group'));
                    const close = true;
                    setHideDialog(close);
                    if (props.onCloseOrOpen) {
                        props.onCloseOrOpen(!close);
                    }
                }
            }
        }
    }, [addAadUserResponse]);

    //Close the dialog after successfully saving a record
    useEffect(() => {
        if (userCreationResponse.status && userCreationResponse.status.toLowerCase() === 'succeeded') {
            const close = true;
            setHideDialog(close);
            if (props.onCloseOrOpen) {
                props.onCloseOrOpen(!close);
            }
        }
    }, [userCreationResponse.status]);

    useEffect(() => {
        dispatch(getTiers({ channel: retailUser.channelName }));
    }, [retailUser.channelName]);

    useEffect(() => {
        setRetailUser(prevState => ({ ...prevState, createdEmailId: userInfo.email }));
    }, []);

    if (props.retailUser) {
        useEffect(() => {
            if (props.retailUser) {
                setRetailUser(props.retailUser);
                setSelectedChannel(props.retailUser.channelId);
                setSelectedSoldTo(props.retailUser.soldToCustomerNbr);
                setSelectedShipTo(props.retailUser.shiptoCustomerNbr);
                setSelectedTier(tiers?.find((tier: any) => tier?.tierName === props?.retailUser?.tierName)?.tierId);
                setSelectedRole(props.retailUser.roleId);
                setFirstName(props.retailUser.firstName);
                setLastName(props.retailUser.lastName);
                setEmail(props.retailUser.email);
                setEnableWarrantyUpdate(props.retailUser.isWarrantyUpdateEnabled);
                setRetailUser(prevState => ({ ...prevState, createdEmailId: userInfo.email }));
            }
        }, [props.retailUser]);

        useEffect(() => {
            setSelectedTier(tiers?.find((tier: any) => tier?.tierName === props?.retailUser?.tierName)?.tierId);
        }, [tiers]);
    }

    useEffect(() => {
        setValidation((prevState: any) => ({ ...prevState, tierName: tiersStatus.status === LoadingState.rejected ? t('tiers_failed') : undefined }));
    }, [tiersStatus.status]);

    const rolesDropdownOptions = roles.map((role: any): IComboBoxOption => ({
        key: role.roleId,
        text: t(role.roleName),
    }));

    const channelDropdownOptions = channels.map((channel: any): IComboBoxOption => ({
        key: channel.channelId,
        text: t(channel.channelName),
    }));

    const tiersDropdownOptions = uniqBy(tiers, (tier: any) => tier.tierId).map((tier: any): IComboBoxOption => ({
        key: tier.tierId,
        text: tier.tierName,
    }));

    function getSoldToData(soldToShipToData: any[]): IComboBoxOption[] {
        let soldToDataList: any[] = soldToShipToData;
        if (props.userInfo && props.userInfo.roleName && props.userInfo.roleName !== 'SuperAdmin') {
            soldToDataList = soldToShipToData.filter(item => item.soldtoCustomerNbr.trim() === props.userInfo.soldToCustomerNbr.trim());
        }

        const uniqueSoldTo = uniqBy(soldToDataList, (item: any) => item.soldtoCustomerName.trim());

        const soldTo = uniqueSoldTo.map((option: any): IComboBoxOption => ({
            key: option.soldtoCustomerNbr,
            text: option.soldtoCustomerName,
        }));

        return soldTo;
    }

    function getShipToData(soldToShipToData: any[], soldToNumber?: string): IComboBoxOption[] {
        if (!soldToNumber) {
            return [];
        }
        
        let uniqueShipTo: any[] = [];
        if (props.userInfo && props.userInfo.roleName && props.userInfo.roleName === 'ShopAdmin') {
            uniqueShipTo = soldToShipToData.filter(item => item.shiptoCustomerNbr.trim() === props.userInfo.shiptoCustomerNbr.trim());
        }
        else {
            const filteredShipTo = soldToShipToData.filter(item => item.soldtoCustomerNbr.trim() === soldToNumber.trim());
            uniqueShipTo = uniqBy(filteredShipTo, (item: any) => item.shiptoCustomerName.trim());
            if(filteredShipTo?.some(item =>filteredShipTo?.filter(obj => obj.shiptoCustomerName.trim() === item.shiptoCustomerName.trim()).length > 1)){
                uniqueShipTo = filteredShipTo?.filter(item => item.shiptoCustomerNbr.trim() === props?.retailUser?.shiptoCustomerNbr);
            }
            if(uniqueShipTo.length === 0){
                uniqueShipTo = uniqBy(filteredShipTo, (item: any) => item.shiptoCustomerName.trim());
            }
        }

        return uniqueShipTo.map((option: any): IComboBoxOption => ({
            key: option.shiptoCustomerNbr,
            text: option.shiptoCustomerName,
        }));
    }

    function validate() {
        let validationError = false;
        setValidation(initialValidationState);

        const inputFields = document.getElementsByTagName('input[type="text"]');

        for (let i = 0; i < inputFields.length; i++) {
            if (inputFields[i] && inputFields[i].innerHTML && inputFields[i].innerHTML?.indexOf('<script>') >= 0) {
                setValidation((prevState: any) => ({ ...prevState, email: t('invalid_text_content') }));
                validationError = true;
                break;
            }
        }

        Object.keys(validation).reduce((acc: any, cur: any) => {
            if (!retailUser[cur]) {
                setValidation((prevState: any) => ({ ...prevState, [cur]: t('required_message', { fieldName: t(snakeCase(cur)) }) }));
                validationError = true;
            }
            return acc;
        }, {});

        if (retailUser.email) {
            if (!validateEmail(retailUser.email)) {
                setValidation((prevState: any) => ({ ...prevState, email: t('invalid_email_address') }));
                validationError = true;
            }
            else if (!isEdit && userInfo.email && userInfo.email !== '' &&
                retailUser.email && retailUser.email !== '' &&
                userInfo.email.trim().toLowerCase() === retailUser.email.trim().toLowerCase()) {
                setValidation((prevState: any) => ({ ...prevState, email: t('validation_invalid_user_action') }));
                validationError = true;
            }
        }
        return validationError ? false : true;
    }

    function onClickClose(evnt: any) {
        const close = true;
        setHideDialog(close);
        if (props.onCloseOrOpen) {
            props.onCloseOrOpen(!close);
        }
        dispatch(clearUserMessage());
        setSubmitted(false);
    }

    function onChildClickClose(event: any) {
        const close = true;
        setHideChildDialog(close);
        setHideDialog(close);
        if (props.onCloseOrOpen) {
            props.onCloseOrOpen(!close);
        }
        setInviteRedeemUrl('');
        dispatch(clearAadUserMessage());
        setSubmitted(false);
    }

    function trimUserInputFields() {
        const retailUserInputs = retailUser;
        Object.keys(retailUserInputs).map(inputVal => retailUserInputs[inputVal] = typeof retailUserInputs[inputVal] === 'string' ? retailUserInputs[inputVal].trim() : retailUserInputs[inputVal]);
        setRetailUser((prevState: any) => ({ ...prevState, retailUserInputs }));
    }

    function onClickSave() {
        trimUserInputFields();
        dispatch(clearAadUserMessage());
        if (!validate()) {
            return;
        }
        if (isAutoOnboarding && !isEdit) {
            dispatch(postAddAadUser(retailUser));
        }
        else {
            dispatch(postUser({ retailUser }));
        }
        setValidation(initialValidationState);
        setSubmitted(true);
    }

    function onClickCopy(event: any) {
        navigator.clipboard.writeText(inviteRedeemUrl);
        setCopyStatus(t('Invitation URL Copied!'));
    }

    function handleMouseLeave(e: any): void {
        setCopyStatus('Copy Link');
    }

    function onChangeTier(event: any, selectedOption?: IComboBoxOption) {
        if (selectedOption) {
            setSelectedTier(selectedOption.key);
            setRetailUser((prevState: any) => ({ ...prevState, tierName: selectedOption.text }));
        }
    }

    function onChangeRole(event: any, selectedOption?: IComboBoxOption) {
        if (selectedOption) {
            setSelectedRole(selectedOption.key);
            setRetailUser((prevState: any) => ({ ...prevState, roleName: selectedOption.text }));
        }
    }

    function onChangeChannel(event: any, selectedOption?: IComboBoxOption) {
        if (selectedOption) {
            setSelectedChannel(selectedOption.key);
            setRetailUser((prevState: any) => ({ ...prevState, channelName: selectedOption.text }));
            setSelectedTier(undefined);
            setRetailUser((prevState: any) => ({ ...prevState, tierId: undefined, tierName: undefined }));
        }
    }

    function onChangeSoldTo(event: any, selectedOption?: IComboBoxOption) {
        if (selectedOption) {
            setSelectedSoldTo(selectedOption.key);
            setRetailUser((prevState: any) => ({ ...prevState, soldToCustomerNbr: selectedOption.key }));
        }
    }

    function onChangeShipTo(event: any, selectedOption?: IComboBoxOption) {
        if (selectedOption) {
            setSelectedShipTo(selectedOption.key);
            setRetailUser((prevState: any) => ({ ...prevState, shiptoCustomerNbr: selectedOption.key }));
        }
    }

    function onChangeWarranty(event: any, checked?: boolean) {
        setRetailUser((prevState: any) => ({ ...retailUser, isWarrantyUpdateEnabled: checked || false }));
        setEnableWarrantyUpdate(checked || false);
    }

    function onChangeFirstName(event: any, newValue?: string) {
        setRetailUser((prevState: any) => ({ ...prevState, firstName: newValue }));
        setFirstName(newValue);
    }

    function onChangeLastName(event: any, newValue?: string) {
        setRetailUser((prevState: any) => ({ ...prevState, lastName: newValue }));
        setLastName(newValue);
    }

    function onChangeEmail(event: any, newValue?: string) {
        setRetailUser((prevState: any) => ({ ...prevState, email: newValue }));
        setEmail(newValue);
    }

    const errorMessage = submitted && userCreationResponse.status && userCreationResponse.status !== 'succeeded' ?
        `${t('error_occurred')} ${userCreationResponse && userCreationResponse.status}` :
        submitted && addAadUserResponse.status && addAadUserResponse.status !== 'succeeded' ?
            `${t('error_occurred')} ${addAadUserResponse && addAadUserResponse.errorInfo}` :
            undefined;

    return (<section>
        <Dialog
            aria-label="AddEditDialog"
            minWidth="550px"
            hidden={hide}
            onDismiss={onClickClose}
            dialogContentProps={{
                titleId: 'add-new-edit-popup',
                type: DialogType.normal,
                closeButtonAriaLabel: t('close'),
                title: isEdit ? t('edit_user') : t('add_new'),
                showCloseButton: true,
                onDismiss: onClickClose,
            }}
            modalProps={{
                titleAriaId: 'add-new-edit-popup',
                isBlocking: true,
                containerClassName: `${Styles.popup}`,
            }}
        >
            <div className="ms-Grid">
                <div className={clsx('ms-Grid-row', Styles.addEditRow)}>
                    <div className={clsx('ms-Grid-col', 'ms-sm12')}>
                        <Message errorMessage={errorMessage} />
                    </div>
                </div>
                <div className={clsx('ms-Grid-row', Styles.addEditRow)}>
                    <div className={clsx('ms-Grid-col', 'ms-sm4')}>
                        <Label className={clsx(Styles.label, Styles.labelRequired)} htmlFor="txt-first-name" >{t('first_name')}</Label>
                    </div>
                    <div className={clsx('ms-Grid-col', 'ms-sm8')}>
                        <TextField
                            ariaLabel={t('first_name')}
                            errorMessage={validation.firstName}
                            id="txt-first-name"
                            name="firstName"
                            onChange={onChangeFirstName}
                            value={firstName}
                        />
                    </div>
                </div>
                <div className={clsx('ms-Grid-row', Styles.addEditRow)}>
                    <div className={clsx('ms-Grid-col', 'ms-sm4')}>
                        <Label className={clsx(Styles.label, Styles.labelRequired)} htmlFor="txt-last-name" >{t('last_name')}</Label>
                    </div>
                    <div className={clsx('ms-Grid-col', 'ms-sm8')}>
                        <TextField
                            ariaLabel={t('lastName')}
                            errorMessage={validation.lastName}
                            id="txt-last-name"
                            name="lastName"
                            onChange={onChangeLastName}
                            value={lastName} />
                    </div>
                </div>
                <div className={clsx('ms-Grid-row', Styles.addEditRow)}>
                    <div className={clsx('ms-Grid-col', 'ms-sm4')}>
                        <Label className={clsx(Styles.label, Styles.labelRequired)} htmlFor="cmb-role-name">{t('role_name')}</Label>
                    </div>
                    <div className={clsx('ms-Grid-col', 'ms-sm8')}>
                        <ComboBox
                            ariaLabel={t('role_name')}
                            autoComplete="on"
                            errorMessage={validation.roleName}
                            id="cmb-role-name"
                            onChange={onChangeRole}
                            options={rolesDropdownOptions}
                            placeholder={t('select_role')}
                            selectedKey={selectedRole}
                            styles={dropdownStyles}
                            useComboBoxAsMenuWidth={true}
                        />
                    </div>
                </div>
                <div className={clsx('ms-Grid-row', Styles.addEditRow)}>
                    <div className={clsx('ms-Grid-col', 'ms-sm4')}>
                        <Label className={clsx(Styles.label, Styles.labelRequired)} htmlFor="txt-email" >{t('email')}</Label>
                    </div>
                    <div className={clsx('ms-Grid-col', 'ms-sm8')}>
                        <TextField
                            ariaLabel={t('email')}
                            errorMessage={validation.email}
                            id="txt-email"
                            name="email"
                            onChange={onChangeEmail}
                            value={email} />
                    </div>
                </div>
                <div className={clsx('ms-Grid-row', Styles.addEditRow)}>
                    <div className={clsx('ms-Grid-col', 'ms-sm4')}>
                        <Label className={clsx(Styles.label, Styles.labelRequired)} htmlFor="cmb-channel">{t('channel')}</Label>
                    </div>
                    <div className={clsx('ms-Grid-col', 'ms-sm8')}>
                        <ComboBox
                            ariaLabel={t('channel')}
                            autoComplete="on"
                            errorMessage={validation.channel}
                            id="cmb-channel"
                            onChange={onChangeChannel}
                            options={channelDropdownOptions}
                            required={true}
                            placeholder={t('select_channel')}
                            selectedKey={selectedChannel}
                            styles={dropdownStyles}
                            useComboBoxAsMenuWidth={true}
                        />
                    </div>
                </div>
                <div className={clsx('ms-Grid-row', Styles.addEditRow)}>
                    <div className={clsx('ms-Grid-col', 'ms-sm4')}>
                        <Label className={clsx(Styles.label, Styles.labelRequired)} htmlFor="cmb-tier">{t('tier_name')}</Label>
                    </div>
                    <div className={clsx('ms-Grid-col', 'ms-sm8')}>
                        <ComboBox
                            ariaLabel={t('tier_name')}
                            autoComplete="on"
                            className={tiersLoading ? Styles.tiers : undefined}
                            disabled={tiersLoading}
                            errorMessage={validation.tierName}
                            id="cmb-tier"
                            onChange={onChangeTier}
                            options={tiersDropdownOptions}
                            placeholder={t('select_tier')}
                            required={true}
                            selectedKey={selectedTier}
                            styles={dropdownStyles}
                            useComboBoxAsMenuWidth={true}
                        />
                        {
                            tiersLoading && (
                                <Spinner
                                    className={Styles.spinnerStyle}
                                    labelPosition="top"
                                    size={SpinnerSize.medium}
                                />
                            )}
                    </div>
                </div>
                <div className={clsx('ms-Grid-row', Styles.addEditRow)}>
                    <div className={clsx('ms-Grid-col', 'ms-sm4')}>
                        <Label className={clsx(Styles.label, Styles.labelRequired)} htmlFor="cmb-soldto-name">{t('sold_to_name')}</Label>
                    </div>
                    <div className={clsx('ms-Grid-col', 'ms-sm8')}>
                        <ComboBox
                            ariaLabel={t('sold_to_name')}
                            autoComplete="on"
                            id="cmb-soldto-name"
                            onChange={onChangeSoldTo}
                            options={getSoldToData(soldToShipToData)}
                            placeholder={t('select_sold_to')}
                            required={true}
                            selectedKey={selectedSoldTo}
                            styles={dropdownStyles}
                            useComboBoxAsMenuWidth={true}
                            errorMessage={validation.soldToCustomerNbr}
                        />
                    </div>
                </div>
                <div className={clsx('ms-Grid-row', Styles.addEditRow)}>
                    <div className={clsx('ms-Grid-col', 'ms-sm4')}>
                        <Label className={clsx(Styles.label, Styles.labelRequired)} htmlFor="cmb-shipto-name">{t('ship_to_name')}</Label>
                    </div>
                    <div className={clsx('ms-Grid-col', 'ms-sm8')}>
                        <ComboBox
                            ariaLabel={t('ship_to_name')}
                            autoComplete="on"
                            errorMessage={validation.shiptoCustomerNbr}
                            id="cmb-shipto-name"
                            onChange={onChangeShipTo}
                            options={getShipToData(soldToShipToData, selectedSoldTo)}
                            placeholder={t('select_ship_to')}
                            required={true}
                            selectedKey={selectedShipTo}
                            styles={dropdownStyles}
                            useComboBoxAsMenuWidth={true}
                        />
                    </div>
                </div>
                {isUserSuperAdmin(userInfo) &&
                    <div className={clsx('ms-Grid-row', Styles.addEditRow)}>
                        <div className={clsx('ms-Grid-col', 'ms-sm4', Styles.checkboxHeader)}>
                            <Label className={clsx(Styles.label, Styles.labelRequired)} htmlFor="chk-warranty">{t('enable_warranty_update')}</Label>
                        </div>
                        <div className={clsx('ms-Grid-col', 'ms-sm8')}>
                            <div className={Styles.checkboxData}>
                                <Checkbox
                                    ariaLabel={t('enable_warranty_update')}
                                    id="chk-warranty"
                                    checked={enableWarrantyUpdate}
                                    onChange={onChangeWarranty}
                                    style={{ display: 'inline-block' }}
                                />
                            </div>
                        </div>
                    </div>
                }
            </div>
            <DialogFooter>
                <button id="btnSave" className={clsx(Styles.buttonPrimary, Styles.button, Styles.commonFocus)} disabled={isCreatingUpdatingUser || isAddingAadUser} onClick={onClickSave}>
                    {isEdit ? t('Update') : t('Save')}
                </button>
                {
                    (isCreatingUpdatingUser || isAddingAadUser) && (
                        <Spinner
                            className={Styles.spinnerStyle}
                            labelPosition="top"
                            size={SpinnerSize.medium}
                        />
                    )}
                <button className={clsx(Styles.buttonSecondary, Styles.button, Styles.commonFocus)} onClick={onClickClose}>{t('cancel')}
                </button>
            </DialogFooter>
        </Dialog>
        {inviteRedeemUrl !== '' &&
            <div className={clsx('ms-Grid-row', Styles.addEditRow)} style={{ textAlign: 'center' }}>
                <Dialog
                    aria-label="invitationDialog"
                    minWidth="550px"
                    hidden={hideChildDialog}
                    onDismiss={onChildClickClose}
                    dialogContentProps={{
                        titleId: 'invitation-url-popup',
                        type: DialogType.normal,
                        closeButtonAriaLabel: t('close'),
                        title: t('Invite Redeem URL'),
                        showCloseButton: true,
                        onDismiss: onChildClickClose,
                    }}
                    modalProps={{
                        titleAriaId: 'invitation-url-popup',
                        isBlocking: true,
                        containerClassName: `${Styles.popup}`,
                    }}
                >
                    <div className="ms-Grid">
                        {inviteRedeemUrl.toLowerCase() === t('user_exists_on_robo_group').toLowerCase() ?
                            <div className={clsx('ms-Grid-row', Styles.addEditRow)}>
                                <div className={clsx('ms-Grid-col', 'ms-sm12', Styles.inviteMessage)}>
                                    {inviteRedeemUrl}
                                </div>
                            </div>
                            : <div className={clsx('ms-Grid-row', Styles.addEditRow)} style={{ marginBottom: '20px' }}>
                                <div className={clsx('ms-Grid-col', 'ms-sm10')}>
                                    <TextField
                                        readOnly={true}
                                        ariaLabel={t('Invitation URL')}
                                        id="txt-inviteRedeem-URL"
                                        name="Invitation URL"
                                        value={inviteRedeemUrl}
                                    />
                                </div>
                                <div className={clsx('ms-Grid-col', 'ms-sm2')}>
                                    <TooltipHost
                                        hostClassName={Styles.modalToolTip}
                                        aria-label={copyStatus}
                                        content={copyStatus}>
                                        <Icon iconName="Copy" onMouseLeave={handleMouseLeave} onClick={onClickCopy} className={Styles.modalToolTipIcon} />
                                    </TooltipHost>
                                </div>
                            </div>}
                    </div>
                    <DialogFooter>
                        <button className={clsx(Styles.buttonPrimary, Styles.button)} onClick={onChildClickClose}>{t('Close')}
                        </button>
                    </DialogFooter>
                </Dialog>
            </div>
        }
    </section >);
}
